import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../api';
import { errorText, operatorApi, type OperatorIdentity, type ProjectOverview } from './api';

/**
 * Betreiber-Konsole unter admin.<Plattform>: Projekte anlegen, Kurzname/Name/Domain ändern, Zugänge vergeben.
 * Bewusst schmal – Mandantendaten (Webinare, Anmeldungen) gibt es hier nicht zu sehen, nur Zählwerte.
 */

const platformDomain = () => window.location.hostname.replace(/^admin\./, '');

function projectUrl(p: ProjectOverview): string {
  const host = p.customDomain ?? `${p.slug}.${platformDomain()}`;
  return `${window.location.protocol}//${host}${window.location.port ? `:${window.location.port}` : ''}`;
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 63);

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');

export function OperatorApp() {
  const [identity, setIdentity] = useState<OperatorIdentity | null | undefined>(undefined);
  const refresh = useCallback(() => {
    operatorApi
      .me()
      .then(setIdentity)
      .catch(() => setIdentity(null));
  }, []);
  useEffect(refresh, [refresh]);
  useEffect(() => {
    document.title = identity ? 'Evercast – Betreiber' : 'Evercast – Betreiber-Anmeldung';
  }, [identity]);

  if (identity === undefined) return null;
  if (identity === null) return <LoginPage onLogin={setIdentity} />;

  async function logout() {
    await operatorApi.logout().catch(() => undefined);
    setIdentity(null);
  }

  return (
    <>
      <header className="admin-bar">
        <span className="admin-bar__brand">Evercast · Betreiber</span>
        <span className="admin-bar__spacer" />
        <span className="muted small">{identity.operator.email}</span>
        <button type="button" className="button button--secondary button--small" onClick={logout}>
          Abmelden
        </button>
      </header>
      <main className="admin-main">
        <Dashboard />
        <PasswordCard />
      </main>
    </>
  );
}

function LoginPage({ onLogin }: { onLogin: (identity: OperatorIdentity) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLogin(await operatorApi.login(email, password));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setError('E-Mail-Adresse oder Passwort stimmen nicht.');
      else if (e instanceof ApiError && e.status === 429) setError('Zu viele Versuche. Bitte in einer Stunde noch einmal.');
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" style={{ maxWidth: 440, paddingTop: 96 }}>
      <div>
        <p className="label label--brand">Evercast</p>
        <h1 style={{ fontSize: 28, marginTop: 8 }}>Betreiber-Konsole</h1>
        <p className="muted">Projekte anlegen und Zugänge vergeben. Die Verwaltung eines Projekts ist unter app.{platformDomain()}/admin.</p>
      </div>
      <form className="card" onSubmit={submit}>
        <div className="field">
          <label htmlFor="email">E-Mail-Adresse</label>
          <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Passwort</label>
          <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button type="submit" className="button" disabled={busy}>
          {busy ? 'Einen Moment …' : 'Anmelden'}
        </button>
      </form>
    </main>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);
    if (next !== repeat) return setError('Die Wiederholung stimmt nicht mit dem neuen Passwort überein.');
    if (next.length < 10) return setError('Das neue Passwort braucht mindestens 10 Zeichen.');
    setBusy(true);
    try {
      await operatorApi.changePassword(current, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      setDone(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} style={{ marginTop: 32 }}>
      <h2 className="card__title">Eigenes Passwort ändern</h2>
      <p className="muted small">Mindestens 10 Zeichen. Andere Sitzungen des Betreiber-Zugangs werden dabei beendet.</p>
      <div className="field">
        <label htmlFor="op-pw-current">Bisheriges Passwort</label>
        <input id="op-pw-current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="op-pw-next">Neues Passwort</label>
          <input id="op-pw-next" type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="op-pw-repeat">Noch einmal</label>
          <input id="op-pw-repeat" type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {done && <p className="muted small">Passwort geändert.</p>}
      <button type="submit" className="button button--secondary" disabled={busy}>
        {busy ? 'Ändert …' : 'Passwort ändern'}
      </button>
    </form>
  );
}

/** Ein frisch erzeugtes Passwort – wird genau einmal gezeigt, danach nirgends mehr. */
function Secret({ title, email, password, onClose }: { title: string; email: string; password: string; onClose: () => void }) {
  return (
    <div className="card" style={{ borderColor: 'var(--brand)' }}>
      <h2 className="card__title">{title}</h2>
      <p>
        Zugang: <strong>{email}</strong>
      </p>
      <p>
        Passwort: <code style={{ fontSize: 18, padding: '4px 8px', background: 'var(--surface-2)', borderRadius: 6 }}>{password}</code>
      </p>
      <p className="muted small">Das Passwort wird nur jetzt angezeigt. Bitte sicher weitergeben; die Person kann es in der Verwaltung ändern.</p>
      <button type="button" className="button button--secondary button--small" onClick={onClose}>
        Verstanden, ausblenden
      </button>
    </div>
  );
}

function Dashboard() {
  const [projects, setProjects] = useState<ProjectOverview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ title: string; email: string; password: string } | null>(null);

  const load = useCallback(() => {
    operatorApi
      .listProjects()
      .then((r) => setProjects(r.projects))
      .catch((e) => setError(errorText(e)));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="stack">
      <div className="page-head">
        <h1>Projekte</h1>
        <p className="muted">
          Jedes Projekt ist ein eigener Mandant mit eigener Adresse, eigenen Zugängen und getrennten Daten. Anlegen, umbenennen, Zugänge vergeben – alles
          Weitere macht das Projekt selbst in seiner Verwaltung.
        </p>
      </div>
      {secret && <Secret {...secret} onClose={() => setSecret(null)} />}
      <NewProject
        onCreated={(p, password, email) => {
          if (password) setSecret({ title: `Projekt „${p.name}" ist angelegt`, email, password });
          load();
        }}
      />
      {error && <div className="error">{error}</div>}
      {projects === null && !error && <p className="muted">Projekte werden geladen …</p>}
      {projects?.length === 0 && <p className="muted">Noch kein Projekt.</p>}
      {projects?.map((p) => (
        <ProjectCard key={p.id} project={p} onChanged={load} onSecret={setSecret} />
      ))}
    </div>
  );
}

function NewProject({ onCreated }: { onCreated: (p: { name: string }, password: string | null, email: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await operatorApi.createProject({ name, slug, ...(email ? { email } : {}), ...(adminName ? { adminName } : {}) });
      onCreated({ name }, result.password, email);
      setName('');
      setSlug('');
      setSlugTouched(false);
      setEmail('');
      setAdminName('');
      setOpen(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div>
        <button type="button" className="button" onClick={() => setOpen(true)}>
          Neues Projekt anlegen
        </button>
      </div>
    );
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card__title">Neues Projekt</h2>
      <div className="field">
        <label htmlFor="np-name">Name des Projekts</label>
        <input
          id="np-name"
          type="text"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          placeholder="z. B. PraxiPet"
        />
      </div>
      <div className="field">
        <label htmlFor="np-slug">Kurzname für die Adresse</label>
        <input
          id="np-slug"
          type="text"
          required
          pattern="[a-z0-9][a-z0-9-]{1,62}"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value.toLowerCase());
          }}
        />
        <p className="faint small">
          Die Teilnehmerseiten liegen dann unter <strong>{slug || '…'}.{platformDomain()}</strong>. Nur Kleinbuchstaben, Ziffern, Bindestriche.
        </p>
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="np-email">E-Mail-Adresse des ersten Zugangs</label>
          <input id="np-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="leer = Zugang später anlegen" />
        </div>
        <div className="field">
          <label htmlFor="np-admin">Name der Person</label>
          <input id="np-admin" type="text" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      <div className="row-actions">
        <button type="submit" className="button" disabled={busy}>
          {busy ? 'Lege an …' : 'Projekt anlegen'}
        </button>
        <button type="button" className="button button--secondary" onClick={() => setOpen(false)}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function ProjectCard({ project, onChanged, onSecret }: { project: ProjectOverview; onChanged: () => void; onSecret: (s: { title: string; email: string; password: string }) => void }) {
  const [mode, setMode] = useState<'view' | 'edit' | 'user'>('view');
  const [name, setName] = useState(project.name);
  const [slug, setSlug] = useState(project.slug);
  const [domain, setDomain] = useState(project.customDomain ?? '');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await operatorApi.updateProject(project.id, { name, slug, customDomain: domain.trim() || null });
      setMode('view');
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function addUser(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await operatorApi.addUser(project.id, email, userName);
      onSecret({ title: result.reset ? `Passwort neu gesetzt für „${project.name}"` : `Zugang für „${project.name}" angelegt`, email, password: result.password });
      setEmail('');
      setUserName('');
      setMode('view');
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title" style={{ margin: 0 }}>
          {project.name} <span className="muted small">· {project.slug}</span>
        </h2>
        <div className="row-actions">
          <button type="button" className="button button--secondary button--small" onClick={() => setMode(mode === 'edit' ? 'view' : 'edit')}>
            Bearbeiten
          </button>
          <button type="button" className="button button--secondary button--small" onClick={() => setMode(mode === 'user' ? 'view' : 'user')}>
            Zugang anlegen
          </button>
        </div>
      </div>
      <p>
        Adresse:{' '}
        <a href={projectUrl(project)} target="_blank" rel="noopener">
          {projectUrl(project).replace(/^https?:\/\//, '')}
        </a>
        {project.customDomain && <span className="muted small"> (eigene Domain; {project.slug}.{platformDomain()} führt ebenfalls hin)</span>}
      </p>
      <p className="muted small">
        {project.webinars} Webinar{project.webinars === 1 ? '' : 'e'} ({project.activeWebinars} aktiv) · {project.registrations} Anmeldung{project.registrations === 1 ? '' : 'en'}, zuletzt {formatDate(project.lastRegistrationAt)} · angelegt {formatDate(project.createdAt)} · Zeitzone {project.timezone}
      </p>
      <p className="small">
        Zugänge:{' '}
        {project.users.length === 0 ? (
          <span className="muted">noch keiner</span>
        ) : (
          project.users.map((u, i) => (
            <span key={u.email}>
              {i > 0 && ', '}
              {u.name ? `${u.name} (${u.email})` : u.email}
              <span className="muted"> – zuletzt angemeldet {formatDate(u.lastLoginAt)}</span>
            </span>
          ))
        )}
      </p>

      {mode === 'edit' && (
        <form onSubmit={save} className="stack" style={{ marginTop: 12 }}>
          <div className="grid-2">
            <div className="field">
              <label htmlFor={`name-${project.id}`}>Name</label>
              <input id={`name-${project.id}`} type="text" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`slug-${project.id}`}>Kurzname</label>
              <input id={`slug-${project.id}`} type="text" required pattern="[a-z0-9][a-z0-9-]{1,62}" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} />
            </div>
          </div>
          <div className="field">
            <label htmlFor={`domain-${project.id}`}>Eigene Domain (optional)</label>
            <input id={`domain-${project.id}`} type="text" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="webinar.praxipet.de" />
            <p className="faint small">
              Vorher beim DNS der Domain einen CNAME oder A-Eintrag auf die Plattform setzen. Das Zertifikat holt der Server beim ersten Aufruf. Ein geänderter Kurzname
              wirkt nach spätestens einer Minute; alte Links mit dem bisherigen Kurznamen gehen dann nicht mehr.
            </p>
          </div>
          {error && <div className="error">{error}</div>}
          <div className="row-actions">
            <button type="submit" className="button" disabled={busy}>
              {busy ? 'Speichert …' : 'Speichern'}
            </button>
            <button type="button" className="button button--secondary" onClick={() => setMode('view')}>
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {mode === 'user' && (
        <form onSubmit={addUser} className="stack" style={{ marginTop: 12 }}>
          <div className="grid-2">
            <div className="field">
              <label htmlFor={`email-${project.id}`}>E-Mail-Adresse</label>
              <input id={`email-${project.id}`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`user-${project.id}`}>Name (optional)</label>
              <input id={`user-${project.id}`} type="text" value={userName} onChange={(e) => setUserName(e.target.value)} />
            </div>
          </div>
          <p className="faint small">Gehört die Adresse schon zu diesem Projekt, wird nur das Passwort neu gesetzt. Adressen anderer Projekte werden abgelehnt.</p>
          {error && <div className="error">{error}</div>}
          <div className="row-actions">
            <button type="submit" className="button" disabled={busy}>
              {busy ? 'Lege an …' : 'Zugang anlegen'}
            </button>
            <button type="button" className="button button--secondary" onClick={() => setMode('view')}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
